import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  convertPriceUnit,
  convertQuantityUnit,
  formatPriceNumber,
  fromM2,
  toM2,
} from "../function";
import * as s from "../InputPage.styles";
import { AnalysisForm, AreaUnit, areaUnitList } from "../machine";
import { useFormattedNumberField } from "./useFormattedNumberField";

interface Props {
  isCommunal: boolean;
  setIsCommunal: Dispatch<SetStateAction<boolean>>;
  formData: AnalysisForm;
  setFormData: Dispatch<SetStateAction<AnalysisForm>>;
  allAreaRef: React.RefObject<HTMLDivElement>;
  areaRef: React.RefObject<HTMLDivElement>;
}

function FarmingAreaSection({
  formData,
  setFormData,
  isCommunal,
  setIsCommunal,
  allAreaRef,
  areaRef,
}: Props) {
  const [openUnitChange, setOpenUnitChange] = useState(false);
  const areaUnitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // 열려있는 드롭다운에 따라 참조할 ref 선택
      const currentRef = areaUnitRef.current;

      if (currentRef && !currentRef.contains(target)) {
        setOpenUnitChange(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const changeAreaUnit = (unit: AreaUnit) => {
    setFormData((prev) => {
      const baseQuantity = convertQuantityUnit(prev.quantity, prev.unit, unit);
      const baseUnitPrice = convertPriceUnit(
        prev.farmAgentUnitPrice,
        prev.unit,
        unit
      );
      return {
        ...prev,
        quantity: baseQuantity,
        farmAgentUnitPrice: baseUnitPrice,
        unit: unit,
      };
    });
    setOpenUnitChange(false);
  };

  const {
    display: allAreaDisplay,
    handleChange: handleAllAreaChange,
    handleBlur: handleAllAreaBlur,
  } = useFormattedNumberField({
    baseValue: toM2(formData.allArea, "평"),
    unit: formData.unit,
    isPrice: false,
    onChangeBaseValue: (m2) => {
      const pyeong = fromM2(m2, "평");
      setFormData((prev) => ({
        ...prev,
        allArea: pyeong,
      }));
    },
  });

  // 자경면적
  const {
    display: areaDisplay,
    setDisplay: setAreaDisplay,
    handleChange: handleAreaChange,
    handleBlur: handleAreaBlur,
  } = useFormattedNumberField({
    baseValue: toM2(formData.area, "평"),
    unit: formData.unit,
    isPrice: false,
    onChangeBaseValue: (m2) => {
      const pyeong = fromM2(m2, "평");
      setFormData((prev) => ({
        ...prev,
        area: pyeong,
      }));
    },
  });

  const farmAgentAreaPyeong = Math.max(formData.allArea - formData.area, 0);
  const farmAgentAreaM2 = toM2(farmAgentAreaPyeong, "평");
  const farmAgentAreaInUnit = fromM2(farmAgentAreaM2, formData.unit);
  const farmAgentAreaDisplay = Number(
    farmAgentAreaInUnit.toFixed(2)
  ).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      farmAgentArea: farmAgentAreaPyeong,
    }));
  }, [farmAgentAreaPyeong, setFormData]);

  return (
    <s.Section>
      <s.SectionTitleContainer>
        <s.SectionTitle>1. 영농 면적</s.SectionTitle>
        <s.UnitWrapper ref={areaUnitRef}>
          <s.UnitChangeButton onClick={() => setOpenUnitChange(true)}>
            <span>단위</span>
            {formData.unit}
          </s.UnitChangeButton>
          {openUnitChange && (
            <s.UnitChangeDropdown>
              {areaUnitList.map((i) => (
                <s.UnitChangeDropdownItem
                  key={i}
                  onClick={() => changeAreaUnit(i)}
                  $active={formData.unit === i}
                >
                  {i}
                  {formData.unit === i && (
                    <img src="/asset/images/check/Check24Green2.svg" alt="" />
                  )}
                </s.UnitChangeDropdownItem>
              ))}
            </s.UnitChangeDropdown>
          )}
        </s.UnitWrapper>
      </s.SectionTitleContainer>
      <s.SectionInner ref={allAreaRef}>
        <s.Label>
          <s.LabelTitle>전체면적</s.LabelTitle>
        </s.Label>
        <s.InputMessageContainer>
          <s.InputWrapper $error={!formData.allArea}>
            <s.Input
              type="text"
              inputMode="decimal"
              placeholder="영농 면적을 입력하세요"
              value={allAreaDisplay}
              onChange={(e) => handleAllAreaChange(e.target.value)}
              onBlur={handleAllAreaBlur}
            />
            <span>{formData.unit}</span>
          </s.InputWrapper>
          {!formData.allArea && (
            <s.InputErrorMessage>
              면적은 필수로 입력해야 합니다.
            </s.InputErrorMessage>
          )}
        </s.InputMessageContainer>
      </s.SectionInner>
      <s.SectionInner $top={8}>
        <s.Checkbox
          onClick={() => {
            setFormData({ ...formData, area: 0 });
            setAreaDisplay("");
            setIsCommunal((prev) => !prev);
          }}
        >
          <img
            src={`/asset/images/check/check-${isCommunal ? "on" : "off"}.svg`}
            alt=""
          />
          영농대행(공동영농)
        </s.Checkbox>
      </s.SectionInner>
      {isCommunal && (
        <>
          <s.SectionInner ref={areaRef}>
            <s.Label>
              <s.LabelTitle>자경면적</s.LabelTitle>
              내가 직접 작업하는 면적을 입력해주세요
            </s.Label>
            <s.InputMessageContainer>
              <s.InputWrapper $error={!formData.area}>
                <s.Input
                  type="text"
                  inputMode="decimal"
                  placeholder="자경 면적을 입력하세요"
                  value={areaDisplay}
                  onChange={(e) => handleAreaChange(e.target.value)}
                  onBlur={handleAreaBlur}
                />
                <span>{formData.unit}</span>
              </s.InputWrapper>
              {formData.allArea < formData.area && (
                <s.InputWarningMessage>
                  자경면적이 전체면적보다 넓습니다
                </s.InputWarningMessage>
              )}
              {!formData.area && (
                <s.InputErrorMessage>
                  자경 면적은 필수로 입력해야 합니다.
                </s.InputErrorMessage>
              )}
            </s.InputMessageContainer>
          </s.SectionInner>
          <s.SectionInner>
            <s.Label>
              <s.LabelTitle>영농대행 면적</s.LabelTitle>
              다른 농가나 대행업체에 맡긴 면적입니다
            </s.Label>
            <s.FixedValue>
              {farmAgentAreaDisplay}
              {formData.unit}
            </s.FixedValue>
          </s.SectionInner>
        </>
      )}
    </s.Section>
  );
}

export default FarmingAreaSection;
